import React, { useEffect } from "react";
import {
  DatePicker,
  ServiceSelector,
  ProfessionalSelector,
  TimeSlotGrid,
  BookingForm,
} from "../";
import {
  Service,
  TimeSlot,
  BookingData,
  Company,
  Professional,
  ServiceType,
} from "../../../types";
import { useBookingWizardState } from "../../../hooks/useBookingWizardState";
import { BackArrow } from "../../shared";

interface BarbershopBookingWizardProps {
  company: Company | null;
  selectedDate: string;
  selectedService: Service | null;
  selectedSubServices?: number[];
  selectedProfessional: Professional | null;
  selectedTimeSlot: TimeSlot | null;
  timeSlots: TimeSlot[];
  professionals: Professional[];
  loading: boolean;
  professionalsLoading: boolean;
  error: string | null;
  phoneNumber: string;
  onDateChange: (date: string) => void;
  onServiceChange: (service: Service) => void;
  onSubServicesChange?: (subServiceIds: number[]) => void;
  onProfessionalChange: (professional: Professional) => void;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  bookAppointment: (bookingData: BookingData) => Promise<void>;
}

export const BarbershopBookingWizard: React.FC<
  BarbershopBookingWizardProps
> = ({
  selectedDate,
  selectedService,
  selectedSubServices = [],
  selectedProfessional,
  selectedTimeSlot,
  timeSlots,
  professionals,
  loading,
  professionalsLoading,
  error,
  phoneNumber,
  onDateChange,
  onServiceChange,
  onSubServicesChange,
  onProfessionalChange,
  onTimeSlotSelect,
  bookAppointment,
}) => {
  const {
    serviceSelectionComplete,
    dateSelectionComplete,
    professionalSelectionComplete,
    setServiceSelectionComplete,
    resetDateSelection,
    resetProfessionalSelection,
    handleDateContinue,
    handleProfessionalContinue,
  } = useBookingWizardState();

  const isTimeBasedService = selectedService?.type === ServiceType.TIME;

  // Reset date selection state only when date is cleared
  useEffect(() => {
    if (!selectedDate) {
      resetDateSelection();
    }
  }, [selectedDate, resetDateSelection]);

  useEffect(() => {
    setServiceSelectionComplete(false);
  }, [selectedService, setServiceSelectionComplete]);

  useEffect(() => {
    resetProfessionalSelection();
  }, [selectedProfessional, resetProfessionalSelection]);

  // Navigation functions
  const handleBackToDate = () => {
    onDateChange("");
    resetDateSelection();
  };

  const handleBackToProfessional = () => {
    onProfessionalChange(null as any);
    resetProfessionalSelection();
  };

  const handleBackToTimeSlot = () => {
    onTimeSlotSelect(null as any);
  };

  // Check if error is about client not found
  const isClientNotFoundError =
    error?.toLowerCase().includes("no client found") ||
    error?.toLowerCase().includes("cliente não encontrado");


  return (
    <div className="w-full mx-auto min-h-[calc(100vh-96px)] flex flex-col">
      <div className="w-full mx-auto lg:p-6 p-4 bg-white flex-1 overflow-y-auto">
        {/* Step 1: Date Selection */}
        {!dateSelectionComplete && (
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={onDateChange}
            onContinue={handleDateContinue}
          />
        )}

        {/* Step 2: Professional Selection */}
        {dateSelectionComplete && !professionalSelectionComplete && (
          <div>
            <BackArrow onClick={handleBackToDate} text="Voltar para data" />
            <ProfessionalSelector
              professionals={professionals}
              selectedProfessional={selectedProfessional}
              onProfessionalSelect={onProfessionalChange}
              onContinue={handleProfessionalContinue}
              loading={professionalsLoading}
            />
          </div>
        )}

        {/* Step 3: Service Selection */}
        {dateSelectionComplete &&
          professionalSelectionComplete &&
          selectedProfessional &&
          !serviceSelectionComplete && (
            <div>
              <BackArrow
                onClick={handleBackToProfessional}
                text="Voltar para profissional"
              />
              <ServiceSelector
                isCarCompany={professionals.length > 1}
                professionalId={selectedProfessional.id}
                selectedService={selectedService}
                selectedSubServices={selectedSubServices}
                onServiceChange={onServiceChange}
                onSubServicesChange={onSubServicesChange}
                onContinue={() => setServiceSelectionComplete(true)}
              />
            </div>
          )}

        {/* Step 4: Time Slot Selection (only for time-based services) */}
        {dateSelectionComplete &&
          selectedProfessional &&
          serviceSelectionComplete &&
          isTimeBasedService &&
          !selectedTimeSlot &&
          !isClientNotFoundError && (
            <div>
              <BackArrow
                onClick={() => setServiceSelectionComplete(false)}
                text="Voltar para serviços"
              />
              {!error && (
                <TimeSlotGrid
                  timeSlots={timeSlots}
                  selectedTimeSlot={selectedTimeSlot}
                  onTimeSlotSelect={onTimeSlotSelect}
                  loading={loading}
                />
              )}
              {error && !isClientNotFoundError && (
                <div className="mt-6 max-w-2xl mx-auto">
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-xl">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <span className="text-red-400 text-xl">⚠️</span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700 font-medium">
                          {error}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Step 4/5: Booking Form */}
        {dateSelectionComplete &&
          serviceSelectionComplete &&
          ((isTimeBasedService && (selectedTimeSlot || isClientNotFoundError)) ||
            !isTimeBasedService) && (
            <div>
              <BackArrow
                onClick={
                  isTimeBasedService && !isClientNotFoundError
                    ? handleBackToTimeSlot
                    : () => setServiceSelectionComplete(false)
                }
                text={
                  isTimeBasedService && !isClientNotFoundError
                    ? "Voltar para horários"
                    : "Voltar para serviços"
                }
              />
              <BookingForm
                selectedDate={selectedDate}
                selectedTimeSlot={selectedTimeSlot}
                selectedService={selectedService!}
                selectedSubServices={selectedSubServices}
                selectedProfessional={selectedProfessional}
                phoneNumber={phoneNumber}
                onSubmit={bookAppointment}
                clientNotFound={isClientNotFoundError}
                loading={loading}
              />
            </div>
          )}
      </div>
    </div>
  );
};
